import { Components } from "@thisgorilla/primate-ds";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { CreatePostModalContext } from "../..";
import { useContainerSize } from "../../../../../../../../hooks/useContainerSize";

const PollPreview = () => {
  const ref = useRef(null);
  const { typography } = useTheme();
  const size = useContainerSize(ref);
  const [height, setHeight] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { formik } = useContext(CreatePostModalContext);
  const values = formik.values;

  const pollOptions = useMemo(() => {
    const options: string[] = [];
    const keys = Object.keys(values);
    keys.forEach((key) => {
      if (key.includes("choice_")) {
        options.push(values[key]);
      }
    });
    return options.filter((option) => option);
  }, [values]);

  useEffect(() => {
    if (ref?.current) {
      setHeight((ref?.current as Element)?.scrollHeight ?? 0);
    }
  }, [size, isOpen, values.question, pollOptions]);

  return (
    <>
      {pollOptions.length > 0 && values.question && (
        <PollPreviewContainer $isShow={isOpen}>
          <PreviewHeading>
            <Components.Label kind="Fill" text="Preview" color="Primary" />
            <Components.IconButton
              kind="Secondary"
              iconType={isOpen ? "ChevronUp" : "ChevronDown"}
              disabled={false}
              size="Small"
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            />
          </PreviewHeading>
          <PreviewBody ref={ref} height={isOpen ? height + 6 : 0}>
            <PreviewContent>
              <Components.Text
                text={values.question || ""}
                style={{ tokenStyle: typography.Body.L }}
                fontWeight="font-weight-semibold"
                lineHeight="line-height-200"
              />
              {pollOptions.map((option, index) => (
                <Container key={index}>
                  <Components.Text
                    text={option}
                    style={{ tokenStyle: typography.Body.L }}
                    renderInSingleLine={true}
                    maxLineLength={1}
                    lineHeight="line-height-200"
                  />
                </Container>
              ))}
            </PreviewContent>
          </PreviewBody>
        </PollPreviewContainer>
      )}
    </>
  );
};

export default PollPreview;

const PollPreviewContainer = styled.div<{ $isShow: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, $isShow }) =>
    $isShow
      ? theme.spacing["spacing-2"].value
      : theme.spacing["spacing-none"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  border: ${({ theme }) => theme.border_width["border-width-thin"].value}rem
    solid ${({ theme }) => theme.Color.border["ui-border-primary-subtle"].value};
  background: ${({ theme }) =>
    theme.Color.background.brand.subtlest["default-hover"].value};
`;

const PreviewHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
`;

const PreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  border: ${({ theme }) => theme.border_width["border-width-thin"].value}rem
    solid ${({ theme }) => theme.Color.border["ui-border-neutral"].value};
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-primary"].value};
`;

const PreviewBody = styled.div<{ height: number }>`
  max-height: ${({ height }) => height + "px"};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const Container = styled.div`
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  background: ${({ theme }) =>
    theme.Color.background.brand.subtlest["default-hover"].value};
  border: 1px solid transparent;

  &:hover {
    background: ${({ theme }) =>
      theme.Color.background.brand.subtlest["default-active"].value};
    border-color: ${({ theme }) =>
      theme.Color.border["ui-border-emphasize-brand"].value};
  }
`;
